import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarSync, Copy, RefreshCw } from "lucide-react";

const STEPS = [
  ["Google / Gmail", "calendar.google.com → Other calendars (+) → From URL → paste the link → Add calendar."],
  ["Outlook", "outlook.com or Outlook app → Add calendar → Subscribe from web → paste the link → Import."],
  ["Apple (iPhone / Mac)", "Tap Open in Apple Calendar below, or Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → paste the link."],
];

export default function CalendarSyncCard({ compact = false }) {
  const [feed, setFeed] = useState(null);
  useEffect(() => { api.get("/me/calendar-feed").then((r) => setFeed(r.data)).catch(() => {}); }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(feed.url); toast.success("Calendar link copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it manually"); }
  };
  const reset = async () => {
    if (!window.confirm("Create a new private link? Calendars using the old link will stop updating.")) return;
    const { data } = await api.post("/me/calendar-feed/reset");
    setFeed(data);
    toast.success("New calendar link created — re-add it to your calendar apps");
  };

  return (
    <section className={compact ? "space-y-3" : "border border-border/60 bg-card p-8 rounded-sm space-y-4"} data-testid="calendar-sync-card">
      <div>
        <div className="flex items-center gap-2 font-medium"><CalendarSync className="w-4 h-4 text-brand" /> Sync to your calendar</div>
        <p className="text-sm text-muted-foreground mt-1">
          A private link that keeps Outlook, Gmail or Apple Calendar up to date with {compact ? "your jobs" : "all bookings"}. Read-only — changes are made here in the app.
        </p>
      </div>
      {feed && (
        <>
          <div className="flex gap-2">
            <Input readOnly value={feed.url} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="calendar-feed-url" />
            <Button variant="outline" onClick={copy} data-testid="calendar-feed-copy" className="rounded-sm gap-2 shrink-0"><Copy className="w-4 h-4" /> Copy</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="sm" variant="outline" className="rounded-sm"><a href={feed.webcal_url} data-testid="calendar-feed-apple">Open in Apple Calendar / Outlook</a></Button>
            <Button asChild size="sm" variant="outline" className="rounded-sm">
              <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feed.webcal_url)}`} target="_blank" rel="noreferrer" data-testid="calendar-feed-google">Add to Google Calendar</a>
            </Button>
            <button type="button" onClick={reset} data-testid="calendar-feed-reset" className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Reset link
            </button>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            {STEPS.map(([k, v]) => <li key={k}><span className="font-medium text-foreground">{k}:</span> {v}</li>)}
          </ul>
          <p className="text-xs text-muted-foreground">Calendar apps refresh subscribed calendars on their own schedule (Google can take up to 12–24 hours; Apple and Outlook usually within an hour or a few hours). Keep this link private.</p>
        </>
      )}
    </section>
  );
}
